/* eslint-disable no-param-reassign */
import Vue from 'vue'
import PadletApi from 'surface/padlet_api'
import { captureException } from 'bits/error_tracker'

export default {
  namespaced: true,
  state: {
    sectionsFetched: false,
    sectionEntities: {},
    pendingSectionId: null,
    sectionBeingEditedId: null,
    actionMenuSectionId: null,
  },
  getters: {
    sections: (state, getters) => getters.sectionIds.map(id => state.sectionEntities[id]),
    sectionIds: state => Object.keys(state.sectionEntities),
    numOfSections: (_, getters) => getters.sectionIds.length,
  },
  mutations: {
    FETCH_SECTIONS_SUCCESS (state, sections) {
      const restorePendingSection = (newSectionEntities) => {
        const pendingSection = state.sectionEntities[state.pendingSectionId]
        if (pendingSection) {
          newSectionEntities[state.pendingSectionId] = pendingSection
        }
      }

      const restoreEditedTitle = (newSectionEntities) => {
        const sectionBeingEdited = state.sectionEntities[state.sectionBeingEditedId]
        const incomingSection = newSectionEntities[state.sectionBeingEditedId]
        if (sectionBeingEdited && incomingSection && sectionBeingEdited.title !== incomingSection.title) {
          incomingSection.title = sectionBeingEdited.title
        }
      }

      if (sections) {
        const newSectionEntities = {}
        sections.forEach(section => {
          newSectionEntities[section.id] = section
        })
        restorePendingSection(newSectionEntities)
        restoreEditedTitle(newSectionEntities)
        state.sectionEntities = newSectionEntities // Trigger state change only once
      }
      state.sectionsFetched = true
    },
    ADD_PENDING_SECTION (state, pendingSection) {
      const tempId = 'new_' + new Date().valueOf()
      state.sectionEntities = {
        ...state.sectionEntities,
        [tempId]: {
          ...pendingSection,
          id: tempId,
        },
      }
      state.pendingSectionId = tempId
    },
    ADD_SECTION (state, section) {
      const newSectionEntities = { ...state.sectionEntities }
      // delete pending section
      delete newSectionEntities[state.pendingSectionId]
      // put new section in place of pending section
      state.sectionEntities = {
        ...newSectionEntities,
        [section.id]: section,
      }
      state.pendingSectionId = null
    },
    UPDATE_SECTION (state, { id, obj }) {
      // Vue.set(state.sectionEntities[sectionId], 'title', title)
      Vue.set(state.sectionEntities, id, obj)
    },
    REMOVE_SECTION (state, id) {
      const newSectionEntities = { ...state.sectionEntities }
      delete newSectionEntities[id]
      state.sectionEntities = newSectionEntities
    },
    SHOW_SECTION_ACTION_MENU (state, sectionId) {
      state.actionMenuSectionId = sectionId
    },
    HIDE_SECTION_ACTION_MENU (state) {
      state.actionMenuSectionId = null
    },
    START_EDITING_SECTION (state, sectionId) {
      state.sectionBeingEditedId = sectionId
    },
    STOP_EDITING_SECTION (state) {
      state.sectionBeingEditedId = null
    },
  },
  actions: {

    fetchSections ({ dispatch, rootState }) {
      PadletApi.WallSection.readAll({ wall: { id: rootState.wall.id } })
        .then((sections) => {
          dispatch('fetchSectionsSuccess', { sections })
        }).catch(captureException)
    },
    fetchSectionsSuccess ({ commit }, { sections }) {
      commit('FETCH_SECTIONS_SUCCESS', sections)
    },
    createSection ({ commit, state, dispatch }, { postToConnectToId, label }) {
      const newSection = {
        from_wish_id: state.postToConnectFromId,
        to_wish_id: postToConnectToId,
        label: label,
      }
      commit('CANCEL_SECTION')
      commit('ADD_PENDING_SECTION', newSection)
      PadletApi.WallSection.create(newSection)
        .then((section) => {
          dispatch('createSectionSuccess', { section })
        })
    },
    createSectionSuccess ({ commit }, { section }) {
      commit('ADD_SECTION', section)
    },

    /* ------------
     * PANEL
     * ------------ */
    showSectionActionMenu ({ commit }, { sectionId }) {
      commit('SHOW_SECTION_ACTION_MENU', sectionId)
    },
    hideSectionActionMenu ({ commit }) {
      commit('HIDE_SECTION_ACTION_MENU')
    },

    /* ------------
     * INTERACTIONS
     * ------------ */
    deleteSection ({ commit, state }, { sectionId }) {
      const existingSection = state.sectionEntities[sectionId]
      commit('STOP_EDITING_SECTION')
      if (existingSection) {
        commit('REMOVE_SECTION', sectionId)
        PadletApi.WallSection.delete(existingSection)
      }
      // oldStore.dispatch({ type: 'DELETE_SECTION', sectionId })
    },
    startEditingSection ({ commit, dispatch, state }, { sectionId }) {
      dispatch('stopEditingSection')
      commit('START_EDITING_SECTION', sectionId)
    },
    stopEditingSection ({ commit, state }) {
      if (state.sectionBeingEditedId && String(state.sectionBeingEditedId).startsWith('new_')) {
        commit('REMOVE_SECTION', state.sectionBeingEditedId)
      }
      commit('STOP_EDITING_SECTION')
    },
    startWithNoSections ({ dispatch, getters }) {
      if (getters.numOfSections === 0) {
        dispatch('startNewSection', { section: { sort_index: window.ww.sectionSorter.getBottomSortIndex() } })
      }
    },
    startNewSection ({ state, commit, dispatch }, { section }) {
      dispatch('stopEditingSection')
      commit('ADD_PENDING_SECTION', section)
      commit('START_EDITING_SECTION', state.pendingSectionId)
    },
    changeSectionTitle ({ commit, state, rootState }, { sectionId, title }) {
      if (state.sectionEntities[sectionId].title !== title) {
        commit('UPDATE_SECTION', {
          id: sectionId,
          obj: {
            ...state.sectionEntities[sectionId],
            title: title,
          },
        })
      }
      if (state.pendingSectionId) { // new section being edited
        PadletApi.WallSection.create({
          ...state.sectionEntities[state.pendingSectionId],
          wall_id: rootState.wall.id,
          title: title,
        }).then((section) => {
          commit('ADD_SECTION', section)
        })
      } else {
        PadletApi.WallSection.update({ ...state.sectionEntities[sectionId], title })
      }
      if (state.sectionBeingEditedId === sectionId) {
        commit('STOP_EDITING_SECTION')
      }
    },
    sortedSection ({ commit, state }, { sectionId, sortIndex }) {
      if (state.sectionEntities[sectionId].sortIndex !== sortIndex) {
        commit('UPDATE_SECTION', {
          id: sectionId,
          obj: {
            ...state.sectionEntities[sectionId],
            sort_index: sortIndex,
          },
        })
        PadletApi.WallSection.update({ ...state.sectionEntities[sectionId], sort_index: sortIndex })
      }
    },
    changeSectionColor () {
    },
    removeSectionColor () {
    },

    /* ------------
     * REMOTE
     * ------------ */
    newSectionRemote ({ commit }, section) {
      commit('ADD_SECTION', section)
    },
    editSectionRemote ({ commit }, section) {
      commit('UPDATE_SECTION', { id: section.id, obj: section })
    },
    deleteSectionRemote ({ commit }, section) {
      commit('REMOVE_SECTION', section.id)
    },
  },
}
